import { useMemo } from "react";
import { evaluateVisibility } from "../../shared/visibility/evaluator";
import { extractDataSources } from "../../shared/visibility/extractDataSources";
import { useVisibilityContext } from "./useVisibilityContext";
import type { DataSourceType } from "@tellescope/visibility-schema";
import type { ShopProduct } from "../../shared/schema";

export interface UseShopProductsFilterResult {
  /** Products whose visibility condition (if any) currently evaluates to true for this enduser. */
  filteredProducts: ShopProduct[];
  /** Whether visibility data required by any product's condition is still loading. */
  isLoading: boolean;
}

/**
 * Filters shop products by their individual visibility conditions. Unlike useItemFilter (one
 * shared condition applied to every item in a list block), each product here carries its own,
 * possibly-absent condition — so this fetches the union of data sources any product's condition
 * needs, then evaluates each product against that one shared context independently.
 *
 * No condition on a product means it's always visible (backward compatible with existing shops).
 * While required data is still loading, products WITH a condition are hidden (fail-closed) to
 * avoid a product flashing in then disappearing, consistent with useBlockVisibility.
 */
export function useShopProductsFilter(products: ShopProduct[]): UseShopProductsFilterResult {
  const requiredSources = useMemo(() => {
    return products.reduce((sources, product) => {
      if (!product.visibility?.condition) return sources;
      extractDataSources(product.visibility.condition).forEach((source) => sources.add(source));
      return sources;
    }, new Set<DataSourceType>());
  }, [products]);

  const { context, isLoading } = useVisibilityContext({ requiredSources });

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const condition = product.visibility?.condition;
      if (!condition) return true;
      if (!context) return false;
      return evaluateVisibility(condition, context);
    });
  }, [products, context]);

  return { filteredProducts, isLoading: requiredSources.size > 0 ? isLoading : false };
}
